import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useChat } from '../hooks/useChat';
import { AuthForm } from './AuthForm';
import { AdminPortal } from './AdminPortal';
import { ConversationSidebar } from './ConversationSidebar';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { McgJazzLogo } from './McgJazzLogo';

export const ChatInterface = ({ darkMode, toggleDarkMode }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showAdminPortal, setShowAdminPortal] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);

  const {
    user, loading: authLoading, error: authError,
    isAuthenticated, isAdmin, register, login, logout, getToken,
  } = useAuth();

  useEffect(() => {
    if (!isAdmin && showAdminPortal) {
      setShowAdminPortal(false);
    }
  }, [isAdmin, showAdminPortal]);

  const {
    messages, conversationId, conversations,
    loading: chatLoading, error: chatError,
    sendMessage, regenerateResponse, loadConversation, startNewConversation,
    deleteConversation, searchConversations,
  } = useChat(getToken(), logout, webSearchEnabled);

  if (authLoading) {
    return <div className="ChatLoading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <AuthForm
        onAuth={async (username, password, isRegister) => {
          if (isRegister) await register(username, password);
          else await login(username, password);
        }}
        loading={authLoading}
        error={authError}
      />
    );
  }

  if (showAdminPortal && isAdmin) {
    return (
      <AdminPortal
        username={user?.username}
        currentUserId={user?.user_id}
        getToken={getToken}
        onBackToChat={() => setShowAdminPortal(false)}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />
    );
  }

  return (
    <div className="AppLayout">
      <ConversationSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
        conversations={conversations}
        activeConversationId={conversationId}
        onSelectConversation={(id) => {
          loadConversation(id);
          // Auto-close on mobile
          if (window.innerWidth <= 640) setSidebarOpen(false);
        }}
        onNewConversation={() => {
          startNewConversation();
          if (window.innerWidth <= 640) setSidebarOpen(false);
        }}
        onDeleteConversation={deleteConversation}
        onSearchConversations={searchConversations}
        username={user?.username}
        onLogout={logout}
        showAdminEntry={isAdmin}
        onOpenAdmin={() => setShowAdminPortal(true)}
      />

      <div className="ChatPane">
        <header className="ChatHeader">
          {!sidebarOpen && (
            <button
              className="ChatButton Sidebar-open-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              ☰
            </button>
          )}
          <div className="ChatHeader-main">
            <div className="ChatHeader-brand">
              <McgJazzLogo size={28} color="#C8102E" />
              <h1 className="ChatTitle">MCG Jazz</h1>
            </div>
            <p className="ChatSubtitle">Explore the rich history of jazz music</p>
          </div>
          <div className="ThemeToggle" title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            <span className="ThemeToggle-icon">🌙</span>
            <label className="ThemeToggle-label">
              <input
                type="checkbox"
                className="ThemeToggle-input"
                checked={!darkMode}
                onChange={toggleDarkMode}
              />
              <span className="ThemeToggle-track" />
              <span className="ThemeToggle-thumb" />
            </label>
            <span className="ThemeToggle-icon">☀️</span>
          </div>
        </header>

        {(chatError || authError) && (
          <div className="ChatError">
            <p>{chatError || authError}</p>
          </div>
        )}

        <main className="ChatMessages" aria-label="Chat messages">
          <MessageList
            messages={messages}
            loading={chatLoading}
            onRegenerate={regenerateResponse}
            onSendSuggestion={sendMessage}
          />
        </main>

        <MessageInput
          onSend={sendMessage}
          disabled={chatLoading}
          webSearchEnabled={webSearchEnabled}
          onToggleWebSearch={() => setWebSearchEnabled((v) => !v)}
        />
      </div>
    </div>
  );
};
